import { useEffect, useState } from 'react';
import { api, type PlatformScan } from '../lib/api.ts';
import { formatStamp, usePrefs } from '../lib/prefs.tsx';
import { formatVin } from '../lib/obd/vin.ts';
import { Term } from './Term.tsx';

/**
 * Every scan on the instance, whichever company it was filed under.
 *
 * Every other history screen reads one company, so a scan filed under the
 * wrong one - a technician whose account sits in the customers company, say -
 * looks lost from everywhere else when it was only misfiled. This is the place
 * that can see it, and the company on each row is the first thing to check.
 *
 * Filtered on the server rather than here, so the list stays a page of rows
 * however many scans the instance collects.
 */

type Filters = {
  q: string;
  company: string;
  by: string;
  from: string;
  to: string;
  codes: '' | 'any' | 'none';
  mil: boolean;
};

const EMPTY: Filters = { q: '', company: '', by: '', from: '', to: '', codes: '', mil: false };
const PAGE = 100;

// The date inputs give the reader's local day. Scans are stored in UTC, so the
// bounds are converted rather than compared as written, or an evening scan in
// Chicago lands on tomorrow.
function utcStamp(localDay: string, addDays = 0): string {
  const d = new Date(`${localDay}T00:00:00`);
  d.setDate(d.getDate() + addDays);
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

function query(f: Filters, limit: number): Record<string, string> {
  const out: Record<string, string> = { limit: String(limit) };
  if (f.q.trim()) out.q = f.q.trim();
  if (f.company) out.company = f.company;
  if (f.by) out.by = f.by;
  if (f.from) out.from = utcStamp(f.from);
  // Inclusive of the chosen day, so the bound is the start of the next one.
  if (f.to) out.to = utcStamp(f.to, 1);
  if (f.codes) out.codes = f.codes;
  if (f.mil) out.mil = '1';
  return out;
}

function vehicleName(s: PlatformScan): string {
  const made = [s.modelYear, s.make, s.model].filter(Boolean).join(' ');
  if (s.label && made) return `${s.label} · ${made}`;
  return s.label || made || (s.vin ? 'Undecoded vehicle' : 'No VIN read');
}

export function PlatformScans({ onOpen }: { onOpen: (scan: PlatformScan) => void }) {
  const { dateFormat } = usePrefs();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  // Typing is held back briefly so each keystroke is not its own request.
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.platformScans>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setFilters((f) => (f.q === q ? f : { ...f, q })), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    api
      .platformScans(query(filters, limit))
      .then((d) => {
        if (stale) return;
        setData(d);
        setError(null);
      })
      .catch((e) => !stale && setError((e as Error).message))
      .finally(() => !stale && setLoading(false));
    return () => {
      stale = true;
    };
  }, [filters, limit]);

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setLimit(PAGE);
    setFilters((f) => ({ ...f, [key]: value }));
  };
  const filtered = JSON.stringify({ ...filters, q }) !== JSON.stringify(EMPTY);

  return (
    <div className="card">
      <div className="row wrap" style={{ gap: 8, marginBottom: 4 }}>
        <h2 style={{ margin: 0, flex: 1 }}>All scans</h2>
        {data && (
          <span className="pill">
            {data.total} scan{data.total === 1 ? '' : 's'}
            {filtered ? ' matched' : ''}
          </span>
        )}
      </div>
      <p className="small dim" style={{ marginTop: 6 }}>
        Scans from every company, newest first. A scan missing from a company's history is usually here under another
        company, which means the account that ran it belongs somewhere else.
      </p>

      <div className="platform-scan-filters">
        <input
          type="search"
          className="platform-filter"
          placeholder="VIN, make, model, company or person"
          aria-label="Search scans"
          value={q}
          onChange={(e) => {
            setLimit(PAGE);
            setQ(e.target.value);
          }}
        />
        <select aria-label="Company" value={filters.company} onChange={(e) => set('company', e.target.value)}>
          <option value="">All companies</option>
          {data?.companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.scans})
            </option>
          ))}
        </select>
        <select aria-label="Scanned by" value={filters.by} onChange={(e) => set('by', e.target.value)}>
          <option value="">Anyone</option>
          {data?.performers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name ?? `Account #${p.id}`}
              {p.company ? ` · ${p.company}` : ''} ({p.scans})
            </option>
          ))}
        </select>
        <select
          aria-label="Trouble codes"
          value={filters.codes}
          onChange={(e) => set('codes', e.target.value as Filters['codes'])}
        >
          <option value="">Any result</option>
          <option value="any">With codes</option>
          <option value="none">Clean</option>
        </select>
        <label className="small platform-date">
          From
          <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => set('from', e.target.value)} />
        </label>
        <label className="small platform-date">
          To
          <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => set('to', e.target.value)} />
        </label>
        <label className="small platform-check">
          <input type="checkbox" checked={filters.mil} onChange={(e) => set('mil', e.target.checked)} />
          MIL on
        </label>
        {filtered && (
          <button
            className="tiny ghost"
            onClick={() => {
              setQ('');
              setLimit(PAGE);
              setFilters(EMPTY);
            }}
          >
            clear filters
          </button>
        )}
      </div>

      {error && <div className="banner error" style={{ marginBottom: 12 }}>{error}</div>}

      {!data ? (
        <p className="dim small">Loading scans…</p>
      ) : data.scans.length === 0 ? (
        <p className="dim small">{filtered ? 'No scans match these filters.' : 'No scans on this instance yet.'}</p>
      ) : (
        <div className="timeline" style={{ opacity: loading ? 0.6 : 1 }}>
          {data.scans.map((s) => (
            <div className="scan-row" key={s.id}>
              <span className="when">
                {formatStamp(s.startedAt, dateFormat)}
                <span className="scan-by">{s.performedByName ? `by ${s.performedByName}` : 'unattributed'}</span>
              </span>

              <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                <span className="platform-scan-vehicle">
                  {vehicleName(s)}
                  <span className="pill tiny-pill" title={`Filed under company #${s.companyId}`}>
                    {s.company}
                  </span>
                </span>
                {s.vin && <span className="mono small dim">{formatVin(s.vin)}</span>}
                <div className="row wrap" style={{ gap: 6 }}>
                  {s.dtcs.length === 0 ? (
                    <span className="pill ok">
                      <i className="dot" />
                      clean
                    </span>
                  ) : (
                    s.dtcs.map((d) => (
                      <span
                        className="mono small"
                        key={`${d.code}-${d.kind}`}
                        title={[d.kind, d.description].filter(Boolean).join(': ')}
                      >
                        {d.code}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="row" style={{ gap: 6 }}>
                <span className={`pill ${s.milOn ? 'warn' : 'ok'}`}>
                  <Term of="MIL">{s.milOn ? 'MIL' : 'no MIL'}</Term>
                </span>
                {/* Opens the vehicle's history inside the company that holds
                    it, the same screen its own staff see. A scan with no VIN
                    has no vehicle to open, so it goes to the company. */}
                <button
                  className="tiny"
                  title={s.vin ? `Open this vehicle's history at ${s.company}` : `Open ${s.company}'s history`}
                  onClick={() => onOpen(s)}
                >
                  open
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {data && data.scans.length < data.total && (
        <div className="row" style={{ justifyContent: 'center', marginTop: 12 }}>
          <button className="tiny ghost" disabled={loading} onClick={() => setLimit((n) => n + PAGE)}>
            show {Math.min(PAGE, data.total - data.scans.length)} more of {data.total - data.scans.length}
          </button>
        </div>
      )}
    </div>
  );
}
